import React from "react";
import * as R from "ramda";
import { withTranslation } from "react-i18next";
import { compose, withProps } from "recompose";
import { connect } from "react-redux";
import { Table, Tag, Popconfirm, Avatar, Icon, Tooltip } from "antd";
import { Content } from "@worcket/common/components/layout";

import * as gtag from "../../../../lib/gtag";
import Page from "../../../../components/display/Page";
import { isLoading } from "../../../../utils/actions";
import { getOptimizedAvatar } from "../../../../utils/images";
import {
  SAVE_OPPORTUNITY_USERS,
  saveOpportunityUsers
} from "../../../../store/actions/opportunity";
import Button from "../../../../components/Button";
import {
  getOpportunityUserDisplayPermissions,
  LIKE_PERMISSION,
  LIKE_SCREENING_RESTRICTED_PERMISSION
} from "./permissions";

import InviteUserModal from "./InviteUserModal";

import styled from "styled-components/macro";

const ALL_PERMISSIONS = ["chat", LIKE_PERMISSION, "write"];

const Section = styled.div`
  margin-bottom: 24px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const AvatarContainer = styled.div`
  display: flex;
  align-items: center;

  .ant-avatar {
    margin-right: 12px;
    flex-shrink: 0;
  }

  .name {
    margin-right: 4px;
  }

  .email {
    color: #adb5bd;
  }

  .header {
    display: flex;
    align-items: center;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-around;
`;

class OpportunityUsers extends React.Component {
  state = {
    isModalVisible: false,
    selectedUser: null
  };

  componentDidMount() {
    gtag.event({
      category: "VacancyUsers",
      action: "VacancyUsers",
      label: this.props.opportunity.id
    });
  }

  removeUser = id => {
    const { guest } = this.props;
    this.saveInvitedUsers(R.reject(R.propEq("id", id))(guest), id);
  };

  editUser = userData => {
    const { guest } = this.props;
    this.saveInvitedUsers(
      guest.map(user =>
        user.id === userData.id ? { ...user, ...userData } : user
      )
    );
  };

  inviteUser = user => {
    const { guest } = this.props;
    this.saveInvitedUsers([...guest, user]);
  };

  saveInvitedUsers = async (users, deletedUser) => {
    const { saveOpportunityUsers, opportunity } = this.props;
    await saveOpportunityUsers({
      id: opportunity.id,
      users: R.map(R.omit(["avatar", "email"]))(users),
      deletedUser
    });
    this.closeModal();
  };

  editProfileUser = selectedUser => {
    this.setState({ selectedUser, isModalVisible: true });
  };

  closeModal = () => {
    this.setState({ isModalVisible: false, selectedUser: null });
  };

  render() {
    const { isModalVisible, selectedUser } = this.state;
    const { usersToInvite, t, guest, owner, loading } = this.props;

    const columns = [
      {
        title: t("opportunity:users.name"),
        dataIndex: "name",
        key: "name",
        width: 300,
        render: (name, user) => (
          <AvatarContainer>
            <Avatar icon="user" src={getOptimizedAvatar(user.avatar)} />
            <div>
              <div className="header">
                <span className="name">{name}</span>
                {user.isOwner && <Tag>{t("global:creator")}</Tag>}
              </div>
              <span className="email">{user.email}</span>
            </div>
          </AvatarContainer>
        )
      },
      {
        title: t("opportunity:users.permissions"),
        dataIndex: "permissions",
        key: "permissions",
        render: (permissions, user) => (
          <span>
            {(user.isOwner
              ? ALL_PERMISSIONS
              : getOpportunityUserDisplayPermissions(permissions)
            ).map(tag => {
              const legend = t(`opportunity:users.permissionLegend.${tag}`, {
                defaultValue: ""
              });
              const isCandidateSelection =
                tag === LIKE_PERMISSION ||
                tag === LIKE_SCREENING_RESTRICTED_PERMISSION;

              return (
                <Tag color="green" key={tag} style={{ cursor: "initial" }}>
                  {t(`opportunity:users.permissionsList.${tag}`)}
                  {legend && isCandidateSelection && (
                    <Tooltip title={legend}>
                      <Icon
                        type="info-circle"
                        style={{ marginLeft: 6, cursor: "help" }}
                      />
                    </Tooltip>
                  )}
                </Tag>
              );
            })}
          </span>
        )
      },
      {
        title: t("opportunity:users.actions"),
        align: "center",
        dataIndex: "actions",
        key: "actions",
        width: 250,
        render: (_, user) =>
          !user.isOwner && (
            <Actions>
              <Button
                size="sm"
                style={{ marginRight: 8 }}
                icon="edit"
                onClick={() => this.editProfileUser(user)}
                disabled={user.deleted}
              >
                {t("opportunity:users.edit")}
              </Button>
              <Popconfirm
                title={t("opportunity:users.deleteUserConfirm")}
                okText={t("global:yes")}
                okType="alert"
                onConfirm={() => this.removeUser(user.id)}
                cancelText={t("global:cancel")}
              >
                <Button
                  size="sm"
                  loading={user.deleted}
                  icon="delete"
                  type="critical"
                >
                  {t("opportunity:users.delete")}
                </Button>
              </Popconfirm>
            </Actions>
          )
      }
    ];

    return (
      <Page>
        <Content>
          <Section>
            <Header>
              <h1 style={{ marginBottom: 0 }}>
                {t("opportunity:users.title")}
              </h1>
              <Button
                type="tertiary"
                onClick={() => {
                  gtag.event({
                    category: "VacancyUsers",
                    action: "VacancyUsers.Invite",
                    label: this.props.opportunity.id
                  });
                  this.setState({ isModalVisible: true });
                }}
              >
                {t("opportunity:users.inviteUser")}
              </Button>
            </Header>
            <p style={{ margin: 5 }}>{t("opportunity:users.description")}</p>
          </Section>
          <Table
            dataSource={[{ isOwner: true, ...owner }, ...guest]}
            columns={columns}
            pagination={false}
            rowKey={record => record.id}
            bordered={true}
            locale={{
              emptyText: t("opportunity:users.noUsers")
            }}
          />
          {isModalVisible && (
            <InviteUserModal
              t={t}
              visible={isModalVisible}
              loading={loading}
              onCancel={this.closeModal}
              handleSubmit={selectedUser ? this.editUser : this.inviteUser}
              usersToInvite={usersToInvite}
              user={selectedUser}
            />
          )}
        </Content>
      </Page>
    );
  }
}

const enhancer = compose(
  withTranslation(["opportunity"]),
  connect(
    state => ({
      opportunity: state.opportunity.selected,
      companyUsers: state.company.users,
      companyId: state.company.id,
      loading: isLoading(SAVE_OPPORTUNITY_USERS, state)
    }),
    {
      saveOpportunityUsers
    }
  ),
  withProps(({ opportunity, companyUsers }) => ({
    guest: R.pipe(
      R.propOr([], "guest"),
      // Add avatar to each user
      R.map(user => {
        const userData = R.pipe(R.find(R.propEq("id", user.id)))(companyUsers);
        if (!userData) return false; // This prevents a bug when the user was deleted from the company but is still in the guest list
        return { ...user, ...R.pick(["avatar", "email"], userData) };
      }),
      R.filter(Boolean)
    )(opportunity),
    owner: R.find(R.propEq("id", opportunity.owner))(companyUsers)
  })),
  withProps(({ opportunity, companyUsers, guest }) => ({
    // List of users to invite to opportunity
    usersToInvite: R.reject(
      R.anyPass([
        // Remove owner and company admin (already have access)
        R.propEq("id", opportunity.owner),
        R.propSatisfies(R.includes("admin"), "roles"),
        // Remove already invited users
        R.propSatisfies(id => guest.find(user => user.id === id), "id")
      ])
    )(companyUsers)
  }))
);

export default enhancer(OpportunityUsers);
